/**
 * Shared display formatters. SINGLE SOURCE OF TRUTH for number and calendar-date
 * formatting on public pages — import from here instead of calling Intl directly.
 * Period parsing (YYYY-MM / YYYY-Qn / YYYY-MM-DD) lives in periods.ts.
 */

const COUNT_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** Whole-number counts, e.g. 5018 → "5,018". */
export function formatCount(value: number): string {
  return COUNT_FORMAT.format(value);
}

const LONG_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});
const LONG_DATE_TIME_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'UTC',
});

const toDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(+date) ? date : null;
};

/** UTC calendar date, e.g. "October 17, 2026"; null for missing/invalid input. */
export function formatLongDate(value: string | null | undefined): string | null {
  const date = toDate(value);
  return date ? LONG_DATE_FORMAT.format(date) : null;
}

/** UTC date and time, e.g. "October 17, 2026 at 10:22 AM UTC". */
export function formatLongDateTime(value: string | null | undefined): string | null {
  const date = toDate(value);
  return date ? `${LONG_DATE_TIME_FORMAT.format(date)} UTC` : null;
}

const MONTH_YEAR_FORMATS = {
  long: new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
  short: new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }),
};
const SHORT_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/** Calendar month (1–12), e.g. (2026, 3) → "March 2026"; `short` → "Mar 2026". */
export function formatMonthYear(year: number, month: number, short = false): string {
  return MONTH_YEAR_FORMATS[short ? 'short' : 'long'].format(new Date(Date.UTC(year, month - 1)));
}

/** UTC calendar date, e.g. "Oct 17, 2026"; null for an invalid date. */
export function formatShortDate(value: Date | string | number): string | null {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? SHORT_DATE_FORMAT.format(date) : null;
}

/** Audio length in seconds → "m:ss"; null when unknown. */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return null;
  return `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;
}

/** Up to two decimals, no grouping (Crop ROI price bands), e.g. 1.2 → "1.2"; null/NaN → "—". */
export function formatPlanNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return (Math.round(Number(value) * 100) / 100).toString();
}

const EXACT_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 20 });

/** Reported values exactly as published, with grouping, e.g. 67569 → "67,569". */
export function formatExact(value: number): string {
  return EXACT_FORMAT.format(value);
}

const decimalFormats = new Map<number, Intl.NumberFormat>();
const compactFormats = new Map<number, Intl.NumberFormat>();
const cached = (cache: Map<number, Intl.NumberFormat>, digits: number, compact: boolean) => {
  let format = cache.get(digits);
  if (!format) {
    format = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: digits,
      ...(compact ? { notation: 'compact' as const } : {}),
    });
    cache.set(digits, format);
  }
  return format;
};

/** Grouped number with up to `digits` decimals (default 2), e.g. 1234.567 → "1,234.57". */
export function formatDecimal(value: number, digits = 2): string {
  return cached(decimalFormats, digits, false).format(value);
}

/** Compact axis/summary number, e.g. 11835 → "11.8K" (digits = max decimals). */
export function formatCompact(value: number, digits = 1): string {
  return cached(compactFormats, digits, true).format(value);
}
